import { useState, useEffect } from 'react';
import { Bell, Settings, Search, AlertCircle, TrendingUp, Package, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { PlanogramItem, DiscontinuedItem, mockPlanogram, mockDiscontinued, mockAisles } from '@/lib/mockData';

type ViewType = 'dashboard' | 'planogram' | 'discontinued' | 'upload';

const PlanoGuard = () => {
  const [planogram, setPlanogram] = useState<PlanogramItem[]>(mockPlanogram);
  const [discontinued, setDiscontinued] = useState<DiscontinuedItem[]>(mockDiscontinued);
  const [activeView, setActiveView] = useState<ViewType>('dashboard');
  const [selectedAisle, setSelectedAisle] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('planogram-app-data');
    if (saved) {
      try {
        const { planogram: p, discontinued: d } = JSON.parse(saved);
        setPlanogram(p);
        setDiscontinued(d);
      } catch (e) {
        console.error('Failed to load saved data:', e);
      }
    }
  }, []);

  // Save to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem('planogram-app-data', JSON.stringify({ planogram, discontinued }));
  }, [planogram, discontinued]);

  const stats = {
    complianceRate: planogram.length > 0 ? Math.round((planogram.filter(p => p.status === 'Compliant').length / planogram.length) * 100) : 0,
    totalItems: planogram.length,
    unauthorizedCount: planogram.filter(p => p.status === 'Unauthorized').length,
    pendingDiscontinued: discontinued.filter(d => d.status === 'Pending').length,
  };

  const filteredPlanogram = planogram.filter(item => {
    const matchesAisle = selectedAisle === 'All' || item.aisle === selectedAisle;
    const matchesSearch = item.sku.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesAisle && matchesSearch;
  });

  const handleMarkCleared = (id: string) => {
    setDiscontinued(prev => prev.map(item =>
      item.id === id ? { ...item, status: 'Cleared' as const, qtyOnShelf: 0 } : item
    ));
    toast.success('Item marked as cleared');
  };

  const handleCSVUpload = (type: 'planogram' | 'discontinued', e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const csv = event.target?.result as string;
        const lines = csv.trim().split('\n');
        if (lines.length < 2) throw new Error('CSV must have header and data rows');

        if (type === 'planogram') {
          const items = lines.slice(1).map(line => {
            const cols = line.split(',').map(c => c.trim());
            return {
              id: Math.random().toString(36).substr(2, 9),
              sku: cols[0],
              name: cols[1],
              aisle: cols[2],
              shelf: parseInt(cols[3]) || 1,
              qtyExpected: parseInt(cols[4]) || 0,
              qtyOnShelf: parseInt(cols[5]) || 0,
              status: (cols[6] || 'Compliant') as 'Compliant' | 'Unauthorized' | 'Low Stock',
            };
          });
          setPlanogram(items);
          toast.success(`Uploaded ${items.length} planogram items`);
        } else {
          const items = lines.slice(1).map(line => {
            const cols = line.split(',').map(c => c.trim());
            return {
              id: 'd' + Math.random().toString(36).substr(2, 9),
              sku: cols[0],
              name: cols[1],
              category: cols[2],
              qtyOnShelf: parseInt(cols[3]) || 0,
              status: 'Pending' as const,
              dateAdded: new Date().toISOString().split('T')[0],
            };
          });
          setDiscontinued(prev => [...prev, ...items]);
          toast.success(`Uploaded ${items.length} discontinued items`);
        }
      } catch (error) {
        toast.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-slate-950/80 border-b border-slate-200 dark:border-slate-800 backdrop-blur">
        <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">PlanoGuard</h1>
            <p className="text-sm text-slate-500">Retail Compliance Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search SKU or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            <Button variant="ghost" size="icon">
              <Bell className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon">
              <Settings className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeView} onValueChange={(v) => setActiveView(v as ViewType)} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="planogram">Planogram</TabsTrigger>
            <TabsTrigger value="discontinued">Discontinued</TabsTrigger>
            <TabsTrigger value="upload">Upload Data</TabsTrigger>
          </TabsList>

          {/* Dashboard */}
          <TabsContent value="dashboard" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Compliance Rate</CardTitle>
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{stats.complianceRate}%</div>
                  <p className="text-xs text-slate-500 mt-1">of items compliant</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Items</CardTitle>
                  <Package className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{stats.totalItems}</div>
                  <p className="text-xs text-slate-500 mt-1">in planogram</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Unauthorized</CardTitle>
                  <AlertCircle className="h-4 w-4 text-red-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{stats.unauthorizedCount}</div>
                  <p className="text-xs text-slate-500 mt-1">items</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Pending Removal</CardTitle>
                  <Clock className="h-4 w-4 text-amber-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white">{stats.pendingDiscontinued}</div>
                  <p className="text-xs text-slate-500 mt-1">items</p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Integrity Alerts</CardTitle>
              </CardHeader>
              <CardContent>
                {stats.unauthorizedCount === 0 ? (
                  <p className="text-sm text-slate-500">No unauthorized items detected. Excellent compliance!</p>
                ) : (
                  <div className="space-y-3">
                    {planogram.filter(p => p.status === 'Unauthorized').map(item => (
                      <div key={item.id} className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-900">
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="font-medium text-slate-900 dark:text-white">{item.name}</p>
                          <p className="text-sm text-slate-500">SKU: {item.sku} • Aisle: {item.aisle} • Qty: {item.qtyOnShelf}</p>
                        </div>
                        <Badge variant="destructive">Unauthorized</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Planogram Manager */}
          <TabsContent value="planogram" className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={selectedAisle === 'All' ? 'default' : 'outline'}
                onClick={() => setSelectedAisle('All')}
                size="sm"
              >
                All Aisles
              </Button>
              {mockAisles.map(aisle => (
                <Button
                  key={aisle}
                  variant={selectedAisle === aisle ? 'default' : 'outline'}
                  onClick={() => setSelectedAisle(aisle)}
                  size="sm"
                >
                  {aisle}
                </Button>
              ))}
            </div>

            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-slate-900 dark:text-white">SKU</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-900 dark:text-white">Product Name</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-900 dark:text-white">Aisle</th>
                      <th className="px-4 py-3 text-center font-medium text-slate-900 dark:text-white">Expected</th>
                      <th className="px-4 py-3 text-center font-medium text-slate-900 dark:text-white">On Shelf</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-900 dark:text-white">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPlanogram.map(item => (
                      <tr
                        key={item.id}
                        className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/50 ${
                          item.status === 'Unauthorized' ? 'bg-red-50 dark:bg-red-950/10' : ''
                        }`}
                      >
                        <td className="px-4 py-3 font-mono text-slate-900 dark:text-white">{item.sku}</td>
                        <td className="px-4 py-3 text-slate-900 dark:text-white">{item.name}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{item.aisle}</td>
                        <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-400">{item.qtyExpected}</td>
                        <td className="px-4 py-3 text-center text-slate-900 dark:text-white font-semibold">{item.qtyOnShelf}</td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={
                              item.status === 'Compliant' ? 'default' :
                              item.status === 'Unauthorized' ? 'destructive' : 'secondary'
                            }
                          >
                            {item.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* Discontinued Report */}
          <TabsContent value="discontinued" className="space-y-4">
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-slate-900 dark:text-white">SKU</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-900 dark:text-white">Product</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-900 dark:text-white">Category</th>
                      <th className="px-4 py-3 text-center font-medium text-slate-900 dark:text-white">Qty</th>
                      <th className="px-4 py-3 text-left font-medium text-slate-900 dark:text-white">Status</th>
                      <th className="px-4 py-3 text-right font-medium text-slate-900 dark:text-white">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {discontinued.map(item => (
                      <tr
                        key={item.id}
                        className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/50 ${
                          item.status === 'Cleared' ? 'opacity-60' : ''
                        }`}
                      >
                        <td className="px-4 py-3 font-mono text-slate-900 dark:text-white">{item.sku}</td>
                        <td className="px-4 py-3 text-slate-900 dark:text-white">{item.name}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{item.category}</td>
                        <td className="px-4 py-3 text-center text-slate-900 dark:text-white">{item.qtyOnShelf}</td>
                        <td className="px-4 py-3">
                          <Badge variant={item.status === 'Pending' ? 'secondary' : 'default'}>
                            {item.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {item.status === 'Pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMarkCleared(item.id)}
                            >
                              Mark Cleared
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* CSV Upload */}
          <TabsContent value="upload" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Planogram Upload */}
              <Card>
                <CardHeader>
                  <CardTitle>Upload Planogram Data</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600 dark:text-slate-400">Expected columns:</p>
                    <pre className="bg-slate-100 dark:bg-slate-900 p-3 rounded text-xs overflow-x-auto">
SKU, Product Name, Aisle, Shelf, Expected Qty, On Shelf, Status
                    </pre>
                  </div>
                  <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-6 text-center hover:bg-slate-50 dark:hover:bg-slate-900/50 cursor-pointer">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => handleCSVUpload('planogram', e)}
                      className="hidden"
                      id="planogram-upload"
                    />
                    <label htmlFor="planogram-upload" className="cursor-pointer block">
                      <p className="font-medium text-slate-900 dark:text-white">Click to upload</p>
                      <p className="text-sm text-slate-500">or drag and drop CSV files</p>
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Discontinued Upload */}
              <Card>
                <CardHeader>
                  <CardTitle>Upload Discontinued Items</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600 dark:text-slate-400">Expected columns:</p>
                    <pre className="bg-slate-100 dark:bg-slate-900 p-3 rounded text-xs overflow-x-auto">
SKU, Product Name, Category, Qty On Shelf
                    </pre>
                  </div>
                  <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-lg p-6 text-center hover:bg-slate-50 dark:hover:bg-slate-900/50 cursor-pointer">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => handleCSVUpload('discontinued', e)}
                      className="hidden"
                      id="discontinued-upload"
                    />
                    <label htmlFor="discontinued-upload" className="cursor-pointer block">
                      <p className="font-medium text-slate-900 dark:text-white">Click to upload</p>
                      <p className="text-sm text-slate-500">or drag and drop CSV files</p>
                    </label>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default PlanoGuard;
